// Service for StcIntRelocation (Internal Relocations) and Grunnlag (Bin Placement Foundation)
import { AuthService } from './authService';
import {
  StcIntRelocationDto,
  StcIntRelocationStatisticsDto,
  CreateStcIntRelocationRequest,
  UpdateStcIntRelocationStatusRequest,
  StcIntRelocationFilterDto,
  GrunnlagItemDto
} from '../types/stcIntRelocation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class StcIntRelocationService {

  // Get all internal relocations
  async getIntRelocations(filter?: StcIntRelocationFilterDto): Promise<StcIntRelocationDto[]> {
    const params = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get internal relocations: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  // Get Grunnlag (Foundation) data - items waiting for put-away
  async getGrunnlag(warehouseId?: string): Promise<StcIntRelocationDto[]> {
    const params = new URLSearchParams();
    if (warehouseId) {
      params.append('warehouseId', warehouseId);
    }

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/grunnlag?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get grunnlag data: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  // Get internal relocations by status
  async getIntRelocationsByStatus(status: number): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/status/${status}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get a specific internal relocation by RelID
  async getIntRelocation(relId: string): Promise<StcIntRelocationDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/${relId}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Create a new internal relocation
  async createIntRelocation(request: CreateStcIntRelocationRequest): Promise<StcIntRelocationDto> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/intrelocations`,
      request
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Update an existing internal relocation
  async updateIntRelocation(relId: string, request: CreateStcIntRelocationRequest): Promise<StcIntRelocationDto> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/intrelocations/${relId}`,
      request
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Delete an internal relocation
  async deleteIntRelocation(relId: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/intrelocations/${relId}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
  }

  // Get internal relocations by operator
  async getIntRelocationsByOperator(operatorId: string): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/operator/${operatorId}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by warehouse
  async getIntRelocationsByWarehouse(warehouseId: string): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/warehouse/${warehouseId}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by item
  async getIntRelocationsByItem(itemNo: string): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/item/${itemNo}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by bin code
  async getIntRelocationsByBinCode(binCode: string): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/bin/${binCode}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by date range
  async getIntRelocationsByDateRange(fromDate: Date, toDate: Date): Promise<StcIntRelocationDto[]> {
    const params = new URLSearchParams({
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
    });
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/daterange?${params.toString()}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by order
  async getIntRelocationsByOrder(orderNo: number): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/order/${orderNo}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by batch number
  async getIntRelocationsByBatch(batchNo: string): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/batch/${batchNo}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Get internal relocations by transaction type
  async getIntRelocationsByTransType(transType: number): Promise<StcIntRelocationDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/transtype/${transType}`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }

  // Update internal relocation status
  async updateIntRelocationStatus(relId: string, status: number): Promise<void> {
    const request: UpdateStcIntRelocationStatusRequest = { status };
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/intrelocations/${relId}/status`,
      request
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
  }

  // Get internal relocation statistics
  async getIntRelocationStatistics(): Promise<StcIntRelocationStatisticsDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelocations/statistics`
    );
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`);
    return await response.json();
  }
}

export const stcIntRelocationService = new StcIntRelocationService();
